import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import { api } from '../../services/api'
import {
  ShoppingBagIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ChartBarIcon,
  PlusIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { CATEGORY_VALUE_TO_LABEL } from '../../constants/product-categories'

interface VendorStats {
  totalProducts: number
  totalOrders: number
  totalRevenue: number
  activeProducts: number
}

interface VendorProduct {
  id: string
  name: string
  category: string
  price: number
  quantity: number
  inStock: boolean
  totalSales: number
  views: number
}

interface VendorOrder {
  id: string
  orderNumber: string
  status: string
  totalAmount: number
  createdAt: string
  user: {
    firstName: string
    lastName: string
    email: string
  }
  items: Array<{
    name: string
    quantity: number
  }>
}

const SouvenirVendorDashboard: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<VendorStats | null>(null)
  const [recentProducts, setRecentProducts] = useState<VendorProduct[]>([])
  const [recentOrders, setRecentOrders] = useState<VendorOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'orders'>('overview')
  const [vendorProfileExists, setVendorProfileExists] = useState<boolean>(true)

  useEffect(() => {
    fetchVendorData()
  }, [])

  const fetchVendorData = async () => {
    try {
      setLoading(true)

      // Fetch vendor stats
      const statsResponse = await api.get('/souvenir-vendors/stats')
      setStats(statsResponse.data)
      setVendorProfileExists(true)

      // Fetch recent products
      const productsResponse = await api.get('/souvenir-products/my-products?isActive=true')
      setRecentProducts(productsResponse.data.slice(0, 5))

      // Fetch recent orders
      const ordersResponse = await api.get('/souvenir-orders/vendor-orders')
      setRecentOrders(ordersResponse.data.slice(0, 5))

    } catch (err: any) {
      console.error('Vendor dashboard fetch error:', err)

      // Check if error is because vendor profile doesn't exist
      if (err.response?.status === 404 && err.response?.data?.message === 'Vendor not found') {
        setVendorProfileExists(false)
        setError(null) // Clear the error, we'll show a custom message
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch vendor data')
      }
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800'
      case 'PREPARING':
        return 'bg-purple-100 text-purple-800'
      case 'READY':
        return 'bg-indigo-100 text-indigo-800'
      case 'SHIPPED':
        return 'bg-green-100 text-green-800'
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!vendorProfileExists) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <ShoppingBagIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vendor Profile Required</h2>
          <p className="text-gray-600 mb-6">
            You need to create a vendor profile before you can access the dashboard.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-2">To create a vendor profile, you can:</h3>
            <ol className="list-decimal list-inside text-blue-800 space-y-1 text-sm">
              <li>Use the API endpoint: <code className="bg-blue-100 px-2 py-1 rounded">POST /api/souvenir-vendors/register</code></li>
              <li>Or contact the administrator to manually create your vendor profile</li>
            </ol>
          </div>
          <p className="text-sm text-gray-500">
            Once your vendor profile is created, you'll be able to manage products, track orders, and view your sales analytics.
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error loading dashboard: {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back! Manage your souvenir products and orders.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.href = '/vendor/products/new'}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition"
          >
            <PlusIcon className="h-5 w-5" />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CubeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-gray-600 text-sm">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <ShoppingBagIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-gray-600 text-sm">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-gray-600 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  RM{Number(stats.totalRevenue || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-gray-600 text-sm">Active Products</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeProducts}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`${
              activeTab === 'products'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition`}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`${
              activeTab === 'orders'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition`}
          >
            Orders
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Recent Products */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Products</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentProducts.length > 0 ? (
                recentProducts.map((product) => (
                  <div key={product.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">{CATEGORY_VALUE_TO_LABEL[product.category] || product.category} • RM{product.price}</p>
                    </div>
                    <div className="flex items-center gap-8 text-sm text-gray-500">
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{product.quantity}</p>
                        <p className="text-xs">Stock</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{product.totalSales}</p>
                        <p className="text-xs">Sold</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{product.views}</p>
                        <p className="text-xs">Views</p>
                      </div>
                      <button
                        onClick={() => window.location.href = `/vendor/products/${product.id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No products yet. Add your first product to get started!
                </div>
              )}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <div key={order.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{order.orderNumber}</p>
                        <p className="text-sm text-gray-500">
                          {order.user.firstName} {order.user.lastName} • {order.items.length} items
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <p className="text-sm font-medium text-gray-900">
                          RM{Number(order.totalAmount).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No orders yet. Your orders will appear here!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'products' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">My Products</h2>
            <button
              onClick={() => window.location.href = '/vendor/products/new'}
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition"
            >
              <PlusIcon className="h-5 w-5" />
              Add Product
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {recentProducts.length > 0 ? (
              recentProducts.map((product) => (
                <div key={product.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{product.name}</p>
                    <p className="text-sm text-gray-500">
                      {CATEGORY_VALUE_TO_LABEL[product.category] || product.category} • RM{typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-8 text-sm text-gray-500">
                    <div className="text-center">
                      <p className="font-medium text-gray-900">{product.quantity}</p>
                      <p className="text-xs">Stock</p>
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-gray-900">{product.totalSales}</p>
                      <p className="text-xs">Sold</p>
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-gray-900">{product.views}</p>
                      <p className="text-xs">Views</p>
                    </div>
                    <button
                      onClick={() => window.location.href = `/vendor/products/${product.id}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <ShoppingBagIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No products yet. Add your first product to get started!</p>
              </div>
            )}
          </div>
          {recentProducts.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => window.location.href = '/vendor-dashboard/products'}
                className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                View All Products
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <div key={order.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{order.orderNumber}</p>
                      <p className="text-sm text-gray-500">
                        {order.user.firstName} {order.user.lastName} • {order.items.length} items
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                      <p className="text-sm font-medium text-gray-900">
                        RM{Number(order.totalAmount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No orders yet. Your orders will appear here!</p>
              </div>
            )}
          </div>
          {recentOrders.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => window.location.href = '/vendor-dashboard/orders'}
                className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                View All Orders
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SouvenirVendorDashboard
